import {
  Box,
  Button,
  Center,
  Collapse,
  Divider,
  Flex,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { ContractionIntensity, ContractionRestingTone, LaborEntryType } from '@hb/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useField } from 'react-final-form'
import { useLaborCharting } from '../../context'

// ─── Helpers ────────────────────────────────────────────────────────────────

const INTENSITY_STEPS: ContractionIntensity[] = [
  ContractionIntensity.MILD,
  ContractionIntensity.MODERATE,
  ContractionIntensity.STRONG,
  ContractionIntensity.VERY_STRONG,
]

const INTENSITY_LABELS: Record<ContractionIntensity, string> = {
  [ContractionIntensity.MILD]: 'Mild',
  [ContractionIntensity.MODERATE]: 'Moderate',
  [ContractionIntensity.STRONG]: 'Strong',
  [ContractionIntensity.VERY_STRONG]: 'Very Strong',
}

const INTENSITY_COLORS: Record<ContractionIntensity, string> = {
  [ContractionIntensity.MILD]: '#68d391',
  [ContractionIntensity.MODERATE]: '#f6ad55',
  [ContractionIntensity.STRONG]: '#fc8181',
  [ContractionIntensity.VERY_STRONG]: '#e53e3e',
}

const intensityToIndex = (v: ContractionIntensity | '') =>
  v ? Math.max(0, INTENSITY_STEPS.indexOf(v)) : 0

const indexToIntensity = (i: number): ContractionIntensity =>
  INTENSITY_STEPS[Math.min(INTENSITY_STEPS.length - 1, Math.max(0, Math.round(i)))]

const formatDuration = (sec: number) => {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

const getNowTimeString = () => {
  const now = new Date()
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
}

// ─── Intensity Slider ────────────────────────────────────────────────────────

const intensityGradient = 'linear-gradient(to right, #68d391, #f6ad55, #fc8181, #e53e3e)'
const intensityGradientDark = 'linear-gradient(to right, #48bb78, #ed8936, #f56565, #c53030)'

const IntensitySlider = () => {
  const { input } = useField<ContractionIntensity>('intensity', {
    subscription: { value: true },
  })
  const [dragging, setDragging] = useState(false)
  const [dragIdx, setDragIdx] = useState(0)

  const currentIdx = intensityToIndex(input.value)
  const displayIdx = dragging ? dragIdx : currentIdx
  const displayIntensity = indexToIntensity(displayIdx)

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <Flex justify="space-between" align="center">
        <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
          INTENSITY
        </Text>
        <Text
          fontSize="lg"
          fontWeight={600}
          fontFamily="hero-new"
          color={INTENSITY_COLORS[displayIntensity]}>
          {INTENSITY_LABELS[displayIntensity]}
        </Text>
      </Flex>
      <Box w="100%" px={2} pb={6}>
        <Slider
          min={0}
          max={3}
          step={1}
          value={dragging ? dragIdx : currentIdx}
          onChangeStart={v => {
            setDragging(true)
            setDragIdx(v)
          }}
          onChange={v => setDragIdx(v)}
          onChangeEnd={v => {
            setDragging(false)
            input.onChange(indexToIntensity(v))
          }}>
          {INTENSITY_STEPS.map((step, i) => (
            <SliderMark
              key={step}
              value={i}
              mt={4}
              transform="translateX(-50%)"
              fontSize="xs"
              whiteSpace="nowrap"
              fontFamily="hero-new"
              color={displayIdx === i ? INTENSITY_COLORS[step] : 'gray.400'}
              fontWeight={displayIdx === i ? 600 : 400}>
              {INTENSITY_LABELS[step]}
            </SliderMark>
          ))}
          <SliderTrack
            h="20px"
            borderRadius="full"
            border="2px solid"
            borderColor="gray.200"
            bg={intensityGradient}
            _dark={{ bg: intensityGradientDark, borderColor: 'gray.600' }}>
            <SliderFilledTrack bg="transparent" />
          </SliderTrack>
          <SliderThumb
            h="32px"
            w="32px"
            borderRadius="full"
            border="2px solid"
            borderColor={INTENSITY_COLORS[displayIntensity]}
            boxShadow="md"
          />
        </Slider>
      </Box>
    </Flex>
  )
}

// ─── Resting Tone ────────────────────────────────────────────────────────────

const RESTING_OPTIONS: { id: ContractionRestingTone; label: string }[] = [
  { id: ContractionRestingTone.SOFT, label: 'Soft' },
  { id: ContractionRestingTone.FIRM, label: 'Firm' },
  { id: ContractionRestingTone.HYPERTONIC, label: 'Hypertonic' },
]

const RestingToneSelector = () => {
  const { input } = useField<ContractionRestingTone>('restingTone', {
    subscription: { value: true },
  })
  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
        RESTING TONE
      </Text>
      <Flex gap={2} w="100%">
        {RESTING_OPTIONS.map(opt => {
          const isActive = input.value === opt.id
          return (
            <Button
              key={opt.id}
              flex={1}
              h="52px"
              fontSize="md"
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'teal' : 'gray'}
              borderRadius={10}
              onClick={() => input.onChange(opt.id)}>
              {opt.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Collection Method ───────────────────────────────────────────────────────

const CollectedViaSelector = () => {
  const { input } = useField<'manual' | 'instrument'>('collectedVia', {
    subscription: { value: true },
  })
  const options: { id: 'manual' | 'instrument'; label: string; sub: string }[] = [
    { id: 'manual', label: 'Manual', sub: 'palpation' },
    { id: 'instrument', label: 'Instrument', sub: 'tocometer' },
  ]
  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
        COLLECTED VIA
      </Text>
      <Flex gap={3} w="100%">
        {options.map(opt => {
          const isActive = input.value === opt.id
          return (
            <Button
              key={opt.id}
              flex={1}
              h="52px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'blue' : 'gray'}
              borderRadius={10}
              onClick={() => input.onChange(opt.id)}>
              <Flex flexFlow="column" align="center" gap={0}>
                <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                  {opt.label}
                </Text>
                <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                  {opt.sub}
                </Text>
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Stats Row ───────────────────────────────────────────────────────────────

const StatPill = ({
  label,
  value,
  unit,
  dimmed,
}: {
  label: string
  value: string
  unit: string
  dimmed?: boolean
}) => (
  <Flex
    flex={1}
    flexFlow="column"
    align="center"
    bg="white"
    _dark={{ bg: '#2a2a2a' }}
    borderRadius={12}
    border="1px solid"
    borderColor="gray.200"
    py={3}
    gap={0.5}
    opacity={dimmed ? 0.4 : 1}>
    <Text
      fontSize="xs"
      fontFamily="hero-new"
      color="gray.500"
      fontWeight={500}
      letterSpacing="wider">
      {label}
    </Text>
    <Text fontSize="2xl" fontFamily="hero-new" fontWeight={600} lineHeight={1.1}>
      {value}
    </Text>
    <Text fontSize="xs" color="gray.400" fontFamily="hero-new">
      {unit}
    </Text>
  </Flex>
)

// ─── Input Pill (manual mode) ────────────────────────────────────────────────

const InputPill = ({
  label,
  unit,
  children,
}: {
  label: string
  unit: string
  children: React.ReactNode
}) => (
  <Flex
    flex={1}
    flexFlow="column"
    align="center"
    bg="white"
    _dark={{ bg: '#2a2a2a', borderColor: 'blue.700' }}
    borderRadius={12}
    border="1px solid"
    borderColor="blue.200"
    py={3}
    px={3}
    gap={1}>
    <Text
      fontSize="xs"
      fontFamily="hero-new"
      color="gray.500"
      fontWeight={500}
      letterSpacing="wider">
      {label}
    </Text>
    {children}
    <Text fontSize="xs" color="gray.400" fontFamily="hero-new">
      {unit}
    </Text>
  </Flex>
)

// ─── Main Component ──────────────────────────────────────────────────────────

export const ContractionEntry = () => {
  const { laborEntries } = useLaborCharting()

  const { input: frequencyInput } = useField<number>('frequency', { subscription: { value: true } })
  const { input: durationInput } = useField<number>('duration', { subscription: { value: true } })
  const { input: notesInput } = useField<string | undefined>('notes', {
    subscription: { value: true },
  })

  // Entry mode
  const [entryMode, setEntryMode] = useState<'timer' | 'manual'>('timer')

  // Manual entry state
  const [manualStartTime, setManualStartTime] = useState(getNowTimeString)

  // Stopwatch state
  const [contractionStart, setContractionStart] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [showNotes, setShowNotes] = useState(!!notesInput.value)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isActive = contractionStart !== null

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current)
    },
    [],
  )

  const handleStart = useCallback(() => {
    const now = Date.now()
    setContractionStart(now)
    setElapsedSeconds(0)

    timerRef.current = setInterval(() => {
      setElapsedSeconds(s => s + 1)
    }, 1000)

    // Auto-calculate frequency from last contraction entry
    const lastContraction = laborEntries.findLast(e => e.type === LaborEntryType.CONTRACTION)
    if (lastContraction) {
      const freqMinutes = (now - lastContraction.startedAt) / 60000
      // Round to nearest 0.5 min, clamp to [0, 30]
      frequencyInput.onChange(Math.min(30, Math.max(0, Math.round(freqMinutes * 2) / 2)))
    }
  }, [laborEntries, frequencyInput])

  const handleEnd = useCallback(() => {
    if (!contractionStart) return
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    const durationSec = Math.round((Date.now() - contractionStart) / 1000)
    durationInput.onChange(durationSec)
    setContractionStart(null)
  }, [contractionStart, durationInput])

  // When manual start time changes, auto-calculate frequency from last contraction
  const handleManualStartTimeChange = useCallback(
    (timeStr: string) => {
      setManualStartTime(timeStr)
      if (!timeStr) return
      const [h, m] = timeStr.split(':').map(Number)
      const startMs = new Date()
      startMs.setHours(h, m, 0, 0)
      const lastContraction = laborEntries.findLast(e => e.type === LaborEntryType.CONTRACTION)
      if (lastContraction) {
        const freqMinutes = (startMs.getTime() - lastContraction.startedAt) / 60000
        frequencyInput.onChange(Math.min(30, Math.max(0, Math.round(freqMinutes * 2) / 2)))
      }
    },
    [laborEntries, frequencyInput],
  )

  const displayDuration = isActive ? elapsedSeconds : (durationInput.value ?? 0)
  const displayFrequency = frequencyInput.value ?? 0

  return (
    <Center pb={8} w="100%">
      <Flex flexFlow="column" w="100%" maxW="500px" gap={5} py={4} px={2} align="center">
        {/* Mode Toggle */}
        <Flex
          w="100%"
          borderRadius={12}
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
          _dark={{ borderColor: 'gray.600' }}>
          {(['timer', 'manual'] as const).map(mode => (
            <Button
              key={mode}
              flex={1}
              h="44px"
              borderRadius={0}
              variant={entryMode === mode ? 'solid' : 'ghost'}
              colorScheme={entryMode === mode ? 'blue' : 'gray'}
              fontFamily="hero-new"
              fontWeight={entryMode === mode ? 600 : 400}
              fontSize="sm"
              onClick={() => setEntryMode(mode)}>
              {mode === 'timer' ? 'Timer' : 'Manual Entry'}
            </Button>
          ))}
        </Flex>

        {entryMode === 'timer' ? (
          <>
            {/* Stats Row – Timer mode */}
            <Flex w="100%" gap={3}>
              <StatPill
                label="FREQUENCY"
                value={displayFrequency > 0 ? String(displayFrequency) : '—'}
                unit="min apart"
                dimmed={displayFrequency === 0 && !isActive}
              />
              <StatPill
                label="DURATION"
                value={
                  displayDuration > 0
                    ? formatDuration(displayDuration)
                    : isActive
                      ? formatDuration(elapsedSeconds)
                      : '—'
                }
                unit={isActive ? 'in progress' : 'seconds'}
                dimmed={displayDuration === 0 && !isActive}
              />
            </Flex>

            {/* Stopwatch Button */}
            <Button
              w="100%"
              h="140px"
              borderRadius={20}
              colorScheme={isActive ? 'red' : 'green'}
              variant="solid"
              fontSize="2xl"
              fontFamily="hero-new"
              fontWeight={600}
              boxShadow="lg"
              onClick={isActive ? handleEnd : handleStart}
              _active={{ transform: 'scale(0.97)' }}
              transition="all 0.15s ease">
              <Flex flexFlow="column" align="center" gap={2}>
                {isActive ? (
                  <>
                    <Text fontSize="3xl" fontWeight={700} lineHeight={1}>
                      {formatDuration(elapsedSeconds)}
                    </Text>
                    <Text fontSize="lg" opacity={0.9}>
                      Tap to End
                    </Text>
                  </>
                ) : (
                  <Text fontSize="2xl">Start Contraction</Text>
                )}
              </Flex>
            </Button>
          </>
        ) : (
          <>
            {/* Stats Row – Manual mode */}
            <Flex w="100%" gap={3}>
              <InputPill label="START TIME" unit="auto-calculates frequency">
                <Input
                  type="time"
                  value={manualStartTime}
                  onChange={e => handleManualStartTimeChange(e.target.value)}
                  fontFamily="hero-new"
                  fontWeight={600}
                  fontSize="md"
                  textAlign="center"
                  border="none"
                  p={0}
                  h="auto"
                  _focus={{ boxShadow: 'none' }}
                />
              </InputPill>

              <InputPill label="DURATION" unit="seconds">
                <NumberInput
                  value={durationInput.value || ''}
                  onChange={(_, v) => durationInput.onChange(isNaN(v) ? 0 : v)}
                  min={0}
                  max={300}
                  step={1}
                  size="md"
                  w="100%">
                  <NumberInputField
                    textAlign="center"
                    fontFamily="hero-new"
                    fontWeight={600}
                    fontSize="xl"
                    px={2}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </InputPill>
            </Flex>
          </>
        )}

        <Divider />

        {/* Intensity Slider */}
        <IntensitySlider />

        <Divider />

        {/* Resting Tone */}
        <RestingToneSelector />

        <Divider />

        {/* Collected Via */}
        <CollectedViaSelector />

        <Divider />

        {/* Notes (collapsible) */}
        <Flex flexFlow="column" w="100%" gap={2}>
          <Flex justify="space-between" align="center">
            <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
              NOTES
            </Text>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              fontFamily="hero-new"
              onClick={() => setShowNotes(v => !v)}>
              {showNotes ? 'Hide' : 'Add Note'}
            </Button>
          </Flex>
          <Collapse in={showNotes} style={{ width: '100%' }}>
            <Textarea
              value={notesInput.value ?? ''}
              onChange={e => notesInput.onChange(e.target.value)}
              placeholder="Additional observations…"
              minH="100px"
              fontFamily="hero-new"
              fontSize="md"
              bg="white"
              _dark={{ bg: '#2a2a2a' }}
              borderRadius={10}
              resize="vertical"
            />
          </Collapse>
        </Flex>
      </Flex>
    </Center>
  )
}
